// services/onboarding.ts

import { Injectable, signal } from '@angular/core';

export interface OnboardingAcademicYear {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
}

export interface OnboardingTerm {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
}

export interface OnboardingState {
  schoolProfileCompleted: boolean;
  academicYear: OnboardingAcademicYear | null;
  term: OnboardingTerm | null;
}

const initialState: OnboardingState = {
  schoolProfileCompleted: false,
  academicYear: null,
  term: null,
};

@Injectable({
  providedIn: 'root',
})
export class OnboardingService {
  private readonly storageKey = 'schoolOnboarding';

  readonly state = signal<OnboardingState>(this.loadState());

  setSchoolProfileCompleted(): void {
    this.updateState({
      schoolProfileCompleted: true,
    });
  }

  setAcademicYear(academicYear: OnboardingAcademicYear): void {
    this.updateState({
      academicYear,
    });
  }

  setTerm(term: OnboardingTerm): void {
    this.updateState({
      term,
    });
  }

  getAcademicYear(): OnboardingAcademicYear | null {
    return this.state().academicYear;
  }

  getTerm(): OnboardingTerm | null {
    return this.state().term;
  }

  reset(): void {
    this.state.set(initialState);
    sessionStorage.removeItem(this.storageKey);
  }

  private updateState(changes: Partial<OnboardingState>): void {
    const updatedState: OnboardingState = {
      ...this.state(),
      ...changes,
    };

    this.state.set(updatedState);

    sessionStorage.setItem(this.storageKey, JSON.stringify(updatedState));
  }

  private loadState(): OnboardingState {
    const storedState = sessionStorage.getItem(this.storageKey);

    if (!storedState) {
      return initialState;
    }

    try {
      const parsedState = JSON.parse(storedState) as Partial<OnboardingState>;

      return {
        ...initialState,
        ...parsedState,
      };
    } catch {
      sessionStorage.removeItem(this.storageKey);
      return initialState;
    }
  }
}
